import {
  ApiErrorCode,
  ApiProjectMerchant,
  ApiProjectSubscriptionPayment,
  ApiProjectSubscriptionStatus,
  ApiService,
  ApiSimpleContract,
  ApiSimpleProjectSubscription,
  ApiSimpleSubscription,
  ApiSubscriptionMerchant,
  ApiSyncType,
} from "@incendium/api";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import { IndentDivider } from "Components/IndentDivider/IndentDivider";
import Loading from "Components/Loading/Loading";
import { SyncCard } from "features/dataConnectors";
import Shopify_logo from "Assets/Images/ThirdParty/shopify_logo_black.png";
import {
  createProjectSubscription,
  getProjectSubscrition,
  listHistorySubscriptions,
  markSubscriptionAsSeen,
  SubscriptionCard,
  SubscriptionPaymentCard,
  SubscriptionVolumePicker,
} from "features/subscription";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import ShopifyConnectButton from "features/dataConnectors/components/ShopifyConnectButton";
import Shopify from "features/dataConnectors/components/Shopify";
import usePublicSubscriptions from "features/subscription/hooks/usePublicSubscriptions";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import { PlatformURL } from "config";
import { useNotification, useSelectedProject } from "Hooks";
import { useExternalSyncs } from "Hooks/useExternalSyncs";
import { friendlyDateTime } from "Helpers/dates";
import { centsToDollars } from "Helpers/numbers";
// import { getCurrencySymbol } from "features/currency";
import SpacedList from "Components/UI/SpacedList";
import CarouselCard from "Components/UI/CarouselCard";
import { OAUTH_REDIRECT_URL } from "features/auth/constants";

interface IHistoryPayment extends ApiProjectSubscriptionPayment {
  name: string;
}

/**
 * read project subsciption, this only returns if there is a piad
 * list available subscriptions, if has a fixed one, only return that, if not fixed then returns all available for the merchant the project is
 */
function PaymentPage() {
  const { search } = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const { selectedProject } = useSelectedProject();
  const [paidSubscription, setPaidSubscription] =
    useState<ApiSimpleProjectSubscription | null>(null);
  const [contract, setContract] = useState<ApiSimpleContract | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [paid, setPaid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [shopifyAppRequired, setShopifyAppRequired] = useState(false);
  const { publicSubscriptions, loading: subsLoading } =
    usePublicSubscriptions();
  const { showErrorNotification } = useNotification();
  const { externalSyncs } = useExternalSyncs();
  const [syncChecked, setSyncChecked] = useState(false);
  const [historyPayments, setHistoryPayments] = useState<IHistoryPayment[]>([]);
  // todo: check, but I think it will always be $
  // const currency = useMemo(
  //   () =>
  //     selectedProject?.currency
  //       ? getCurrencySymbol(selectedProject?.currency)
  //       : "$",
  //   [selectedProject],
  // );
  const currency = "$";

  useEffect(() => {
    if (syncChecked || !externalSyncs || !selectedProject) {
      return;
    }

    if (selectedProject.merchant === ApiProjectMerchant.SHOPIFY) {
      const hasShopIdx = externalSyncs.findIndex(
        (f) => f.type === ApiSyncType.SHOPIFY_API,
      );

      if (hasShopIdx < 0) {
        setShopifyAppRequired(true);
      }
    }
    setSyncChecked(true);
  }, [selectedProject, syncChecked, externalSyncs]);

  const loadPaidSubcription = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      const res = await getProjectSubscrition(projectId);
      setPaidSubscription(res);
      setContract(res.subscription?.contract || {});
    } catch (error: any) {
      const t = await error.json();
      const errorCode = t.details?.[0].code;
      if (errorCode === ApiErrorCode.ERROR_CODE_SHOPIFY_APP_REQUIRED) {
        setShopifyAppRequired(true);
      }
    }
    setLoading(false);
  }, []);

  const markAsSeen = useCallback((projectSubscriptionId: string) => {
    markSubscriptionAsSeen(projectSubscriptionId);
  }, []);

  useEffect(() => {
    loadPaidSubcription(Number(projectId));
  }, [projectId, loadPaidSubcription]);

  const loadHistorySubscriptions = useCallback(async () => {
    const res = await listHistorySubscriptions(Number(projectId));
    setHistoryPayments(
      res.result?.flatMap(
        (r) =>
          r.payments?.map((p) => ({
            date: p.paidAt,
            amount: p.amount,
            name: r?.productName || "",
          })) || [],
      ) || [],
    );
  }, [projectId]);

  useEffect(() => {
    if (paidSubscription?.id) {
      markAsSeen(paidSubscription.id);

      if (paidSubscription?.paymentLinkQr) {
        const base64Data = paidSubscription?.paymentLinkQr.startsWith(
          "data:image/png;base64,",
        )
          ? paidSubscription?.paymentLinkQr
          : `data:image/png;base64,${paidSubscription?.paymentLinkQr}`;

        setImageSrc(base64Data);
      }

      if (
        paidSubscription?.status ===
        ApiProjectSubscriptionStatus.PROJECT_SUBSCRIPTION_STATUS_PAID
      ) {
        loadHistorySubscriptions();
      }
    }
  }, [paidSubscription, markAsSeen]);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    if (searchParams.has("success")) {
      if (searchParams.get("success") === "true") {
        setPaid(true);
      } else {
        setPaid(false);
      }
      return;
    }
    if (
      paidSubscription?.status ===
      ApiProjectSubscriptionStatus.PROJECT_SUBSCRIPTION_STATUS_PAID
    ) {
      setPaid(true);
    }
  }, [paidSubscription, search]);

  const onPayClick = useCallback(
    async (sub?: ApiSimpleSubscription, isAnnual?: boolean) => {
      if (!sub?.id) {
        return;
      }

      try {
        const res = await createProjectSubscription(
          Number(projectId),
          sub.id,
          `${PlatformURL}/clients/${selectedProject?.clientId}/projects/${projectId}/account/payment?success=true`,
          false,
          0,
          isAnnual,
        );
        window.location.href = res.paymentLink || "";
      } catch (error: any) {
        const t = await error.json();
        showErrorNotification(t.message);
        const errorCode = t.details?.[0].code;
        if (errorCode === ApiErrorCode.ERROR_CODE_SHOPIFY_APP_REQUIRED) {
          setShopifyAppRequired(true);
        }
      }
    },
    [selectedProject, projectId, showErrorNotification],
  );

  const onSelect = useCallback(
    async (sub, isAnnual?: boolean) => {
      setOpen(false);
      await onPayClick(sub, isAnnual);
    },
    [onPayClick],
  );

  const planCards = useMemo(
    () =>
      publicSubscriptions.map((sub) => {
        const isCurrent = sub.id === paidSubscription?.subscription?.id;
        return (
          <SubscriptionCard
            key={sub.id}
            subscription={sub}
            dense
            onSelect={onSelect}
            isCurrent={isCurrent}
            currentIsAnnual={isCurrent ? !!paidSubscription?.isAnnual : undefined}
          />
        );
      }),
    [
      publicSubscriptions,
      paidSubscription?.subscription?.id,
      paidSubscription?.isAnnual,
      onSelect,
    ],
  );

  if (loading || subsLoading || !syncChecked) {
    return (
      <>
        <Loading fullHeight />
      </>
    );
  }

  if (!shopifyAppRequired && !paidSubscription?.subscription) {
    if (publicSubscriptions.length === 1) {
      const assignedSubscription = publicSubscriptions[0];
      return (
        <GlassCard>
          <Grid
            container
            spacing={4}
            alignItems="center"
            justifyContent="center"
          >
            <Grid item xs={1}></Grid>
            <Grid item xs={4}>
              <Stack spacing={2}>
                <Typography variant="h5" fontWeight={600}>
                  Your Package
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Pay to unlock Incendium Ai. Review your package details and
                  proceed with payment to get started.
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Box sx={{ maxWidth: 400, width: "100%" }}>
                  <SubscriptionCard
                    subscription={assignedSubscription}
                    dense
                    onSelect={onSelect}
                    isCurrent={
                      assignedSubscription.id ===
                      paidSubscription?.subscription?.id
                    }
                    currentIsAnnual={
                      assignedSubscription.id ===
                        paidSubscription?.subscription?.id
                        ? !!paidSubscription?.isAnnual
                        : undefined
                    }
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </GlassCard>
      );
    }

    return (
      <Stack spacing={3}>
        <SubscriptionVolumePicker
          subscriptions={publicSubscriptions}
          currentSubscriptionId={paidSubscription?.subscription?.id}
          currentIsAnnual={!!paidSubscription?.isAnnual}
          onSelect={onSelect}
        />
        <CarouselCard
          title="Or browse all plans"
          emptyMessage="No plans available"
          inview={4}
          itemMinWidth={280}
          items={planCards}
        />
      </Stack>
    );
  }

  return (
    <>
      <GlassCard>
        <Stack direction="row" justifyContent={"space-between"}>
          <Typography mb={2} variant="subtitle1">
            Billing & Payment
          </Typography>
          {!shopifyAppRequired && (
            <Button size="small" onClick={() => setOpen(true)}>
              Change Plan
            </Button>
          )}
        </Stack>

        <IndentDivider />

        {shopifyAppRequired ? (
          <>
            <Typography variant="subtitle1" mt={2}>
              You must connect your shopify store before processeding.
            </Typography>
            <Typography variant="body1" mb={2}>
              To do that you can click the connect button below, then enter your
              shop name and thats it.
            </Typography>
            <Box sx={{ width: 500 }}>
              <SyncCard
                logo={Shopify_logo}
                type={ApiSyncType.SHOPIFY_API}
                title="Shopify App"
                description="Connect with your Shopify store for more accurate tracking and ecommerce insights."
                service={ApiService.SHOPIFY}
                configComponent={Shopify}
                state={OAUTH_REDIRECT_URL}
                imageSx={{ maxHeight: 32 }}
                ConnectButton={ShopifyConnectButton}
                linkOnConnect
                handleOnConfigure={() => loadPaidSubcription(Number(projectId))}
              />
            </Box>
          </>
        ) : paidSubscription?.subscription ? (
          <Grid container spacing={5} mt={0}>
            <Grid item xs={7}>
              <SpacedList>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyPayments.map((payment) => (
                    <TableRow key={payment.paidAt?.toISOString()}>
                      <TableCell>{friendlyDateTime(payment.paidAt)}</TableCell>
                      <TableCell>{payment.name}</TableCell>
                      <TableCell>
                        {currency}
                        {centsToDollars(payment.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </SpacedList>
            </Grid>
            <Grid item xs={5}>
              <SubscriptionPaymentCard
                name={paidSubscription.subscription.product?.name || "Starter"}
                description={
                  paidSubscription.subscription.product?.description || ""
                }
                planDescription={
                  paidSubscription.subscription.description || ""
                }
                price={paidSubscription.subscription.price || 0}
                annualPrice={paidSubscription.subscription.annualPrice || 0}
                isAnnual={!!paidSubscription.isAnnual}
                qr={imageSrc}
                terms={contract?.body || ""}
                paid={paid}
                merchant={
                  paidSubscription.subscription.merchant ||
                  ApiSubscriptionMerchant.SUBSCRIPTION_MERCHANT_STRIPE
                }
                onPayClick={() =>
                  onPayClick(
                    paidSubscription.subscription,
                    paidSubscription.isAnnual
                  )
                }
              />
            </Grid>
          </Grid>
        ) : (
          <></>
        )}
      </GlassCard>
      <CustomDialog open={open} fullWidth maxWidth="lg">
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <SubscriptionVolumePicker
              subscriptions={publicSubscriptions}
              currentSubscriptionId={paidSubscription?.subscription?.id}
              currentIsAnnual={!!paidSubscription?.isAnnual}
              onSelect={onSelect}
            />
            <CarouselCard
              title="Or browse all plans"
              emptyMessage="No plans available"
              inview={3}
              itemMinWidth={260}
              items={planCards}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </CustomDialog>
    </>
  );
}

export default PaymentPage;
